/**
 * "Summarize with AI" — pure helpers (no React, no window) that turn a page's
 * canonical URL plus the Site Settings `ai` group into the provider links the
 * footer renders, and the "Hey AI, learn about us" href.
 *
 * Provider URLs and the default prompt live in ./ai-providers.
 */
import type { AiProviderId } from './ai-providers';
import { AI_PROVIDERS, AI_PROVIDER_IDS, DEFAULT_SUMMARIZE_PROMPT, SUMMARIZE_PROMPT_HARD_MAX } from './ai-providers';

export interface SummarizeContext {
  /** Canonical, absolute URL of the current page (see canonicalPageUrl). */
  url: string;
  title?: string;
}

/**
 * Strip every `<` / `>` (Claude drops them, and we never want markup-looking
 * text in a prompt) and collapse all whitespace — newlines and tabs included —
 * to single spaces.
 */
export function sanitizePrompt(text: string): string {
  return text.replace(/[<>]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Fill `{url}` / `{title}` in an admin-supplied template. A template that
 * forgot `{url}` gets the URL appended.
 *
 * SUMMARIZE_PROMPT_HARD_MAX caps the editor's template, not the result: a
 * filled custom template over the cap falls back to the default template
 * rather than being truncated. The URL is never cut, so an absurdly long
 * pathname can still push even the default-built prompt past the cap — that
 * is bounded only by the request-URL length the server accepts.
 */
export function buildSummarizePrompt(template: string | null | undefined, ctx: SummarizeContext): string {
  const source = typeof template === 'string' && template.trim() !== '' ? template : DEFAULT_SUMMARIZE_PROMPT;
  let filled = source.split('{url}').join(ctx.url).split('{title}').join(ctx.title ?? '');
  if (!filled.includes(ctx.url)) filled += ' ' + ctx.url;
  const prompt = sanitizePrompt(filled);
  // Guard on `source`, not on length alone: with a URL over ~1,400 chars the
  // default itself exceeds the cap and we would recurse forever (RangeError
  // during SSR of any page whose pathname a bot padded out).
  if (prompt.length > SUMMARIZE_PROMPT_HARD_MAX && source !== DEFAULT_SUMMARIZE_PROMPT) {
    return buildSummarizePrompt(DEFAULT_SUMMARIZE_PROMPT, ctx);
  }
  return prompt;
}

export interface SummarizeLink {
  id: AiProviderId;
  label: string;
  href: string;
}

/**
 * One link per enabled provider, in AI_PROVIDERS order. `opts.providers`
 * undefined = all; `[]` = none.
 */
export function buildSummarizeLinks(
  ctx: SummarizeContext,
  opts?: { template?: string | null; providers?: readonly AiProviderId[] },
): SummarizeLink[] {
  const prompt = buildSummarizePrompt(opts?.template, ctx);
  const encoded = encodeURIComponent(prompt);
  const allowed = opts?.providers;
  return AI_PROVIDERS.filter((p) => allowed === undefined || allowed.includes(p.id)).map((p) => ({
    id: p.id,
    label: p.label,
    href: p.template.split('{prompt}').join(encoded),
  }));
}

/**
 * Absolute canonical URL for a pathname: no query/hash, no duplicate or
 * trailing slashes (the root keeps its single `/`). A missing pathname is
 * the root.
 */
export function canonicalPageUrl(siteUrl: string, pathname: string | null | undefined): string {
  const base = siteUrl.trim().replace(/\/+$/, '');
  let path = (pathname ?? '').replace(/[?#][\s\S]*$/, '');
  path = '/' + path;
  path = path.replace(/\/{2,}/g, '/');
  if (path.length > 1) path = path.replace(/\/+$/, '');
  return base + path;
}

/**
 * The id behind a Payload relationship value: a bare id (depth 0), a
 * populated doc ({ id, ... }, depth >= 1), or a numeric string. null when unset
 * or unrecognisable. Shared by the layout, the Pages flush hook and the promote
 * deletion guard so they cannot drift on what "the chosen info page" means.
 */
export function relationId(value: unknown): number | null {
  const raw = typeof value === 'object' && value !== null ? (value as { id?: unknown }).id : value;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number(raw);
  return null;
}

export interface AiInfoLinkInput {
  infoPageSlug?: string | null;
  llmsTxtAvailable: boolean;
}

/**
 * Slug shapes we will put in a same-origin href: letters, digits, hyphens and
 * single interior slashes (nested pages such as help/getting-started), case
 * preserved. The Pages slug field is only required + unique — no charset
 * check — so without this an editor typo like \evil.com would render as
 * href="/\evil.com", which browsers normalise to the protocol-relative
 * "//evil.com": an external navigation from every page of the site. Anything
 * that fails the pattern is treated as "no slug" and falls through to
 * /llms.txt (or nothing).
 */
const SAFE_SLUG_RE = /^[a-z0-9]+(?:[-/][a-z0-9]+)*$/i;

/** "Hey AI, learn about us" target: the chosen CMS page, else /llms.txt, else nothing. */
export function aiInfoHref(input: AiInfoLinkInput): string | null {
  const trimmed = typeof input.infoPageSlug === 'string' ? input.infoPageSlug.trim().replace(/^\/+|\/+$/g, '') : '';
  const slug = SAFE_SLUG_RE.test(trimmed) ? trimmed : '';
  if (slug !== '') return slug === 'home' ? '/' : '/' + slug;
  return input.llmsTxtAvailable ? '/llms.txt' : null;
}

export interface AiSettings {
  showSummarize: boolean;
  promptTemplate: string;
  providers: AiProviderId[];
  infoHref: string | null;
  siteUrl: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Normalise Site Settings' `ai` group (which may be missing, null, or have
 * NULL columns on rows that predate the fields) into a fully-resolved shape.
 * Every unset value means "on" / "default".
 */
export function resolveAiSettings(raw: unknown, ctx: { siteUrl: string; allowCrawling: boolean }): AiSettings {
  const ai = isRecord(raw) ? raw : {};

  const showSummarize = ai.showSummarize === false ? false : true;

  const promptTemplate =
    typeof ai.summarizePrompt === 'string' && ai.summarizePrompt.trim() !== ''
      ? ai.summarizePrompt
      : DEFAULT_SUMMARIZE_PROMPT;

  const rawProviders = isRecord(ai.providers) ? ai.providers : {};
  const providers = AI_PROVIDER_IDS.filter((id) => rawProviders[id] !== false);

  let infoPageSlug: string | null = null;
  const infoPage = ai.infoPage;
  if (isRecord(infoPage)) {
    const slug = infoPage.slug;
    const status = infoPage._status;
    if (typeof slug === 'string' && (status === undefined || status === 'published')) {
      infoPageSlug = slug;
    }
  }
  const infoHref = aiInfoHref({ infoPageSlug, llmsTxtAvailable: ctx.allowCrawling });

  return { showSummarize, promptTemplate, providers, infoHref, siteUrl: ctx.siteUrl };
}
